import { useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';

import { Box, Button, Link, MoreVertIcon, NestedMenu, Portal, Snackbar, Tooltip } from '@parspec/pixel';

import ReuseDatasheetDialog from '../shared/ReuseDatsheetDialog';
import AddReuseDocument from '../shared/AddReuseDocument';
import AutosearchLoaderDialog from '../../shared/AutosearchLoaderDialog';
import KebabNestedMenu from 'src/features/BOM/shared/KebabNestedMenu';

import { autoSearchMsg } from '../../shared/constants';
import { useDatasheetAutoSearchMutation, useInstallGuideSearchMutation, useOmAutoSearchMutation, useWarrantySearchMutation } from '../queries';
import { SelectedDocType, SelectedSearchType } from '../shared/constants';
import { GET_AUTO_SEARCH_OPTIONS } from '../shared/utils/utils';
import { getOmLoadTime } from './helper';
import { ReuseDialogType } from '../shared/utils';
import { OmDataResponse } from '../queries/apiTypes';
import { useTrackIntercomEvent } from '../../shared/hooks/useIntercomHook';
import { BodResponseWithComponents } from '../shared/types';
import ImportProducts from '../shared/ImportProducts';


interface OmTabHeaderProps {
    selectedItems: number[];
    unCheckSelectedLineItems: () => void;
    tableData: BodResponseWithComponents[] | OmDataResponse[];
    setOpenManageColumnsPopup: React.Dispatch<React.SetStateAction<boolean>>;
    hasOption?: boolean
}

const anchorOrigin = {
    vertical: 'top',
    horizontal: 'left'
} as const;
const transformOrigin = {
    vertical: 'top',
    horizontal: 'right'
} as const;


const DocumentTabHeader = ({ selectedItems, unCheckSelectedLineItems, tableData, setOpenManageColumnsPopup, hasOption }: OmTabHeaderProps) => {
    const { bomId } = useParams();
    const [openReuseDatasheetDialog, setOpenReuseDatasheetDialog] = useState(false);
    const [showAutosearchDialog, setShowAutoSearchDialog] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [openAddReuseDialog, setOpenAddReuseDialog] = useState(false);
    const [selectedDocType, setSelectedDocType] = useState('');
    const [selectedReuseDialogType, setSelectedReuseDialogType] = useState<ReuseDialogType>(ReuseDialogType.WARRANTY);

    const { mutateAsync: datasheetAutoSearch, isLoading: datasheetAutoSearchLoading } = useDatasheetAutoSearchMutation();
    const { mutateAsync: omAutoSearch, isLoading: omAutoSearchLoading } = useOmAutoSearchMutation();
    const { mutateAsync: installGuideAutoSearch, isLoading: instAutoSearchLoading } = useInstallGuideSearchMutation();
    const { mutateAsync: warrantyAutoSearch, isLoading: warrantyAutoSearchLoading } = useWarrantySearchMutation();
    const { trackIntercomEvent } = useTrackIntercomEvent();

    const handleAutoSearchDialog = () => setShowAutoSearchDialog((old) => !old);

    const onAutoSearch = async (docType: SelectedDocType, type: SelectedSearchType) => {
        setSelectedDocType(docType);
        handleAutoSearchDialog();
        try {
            const data = {
                bomId: Number(bomId),
                input: {
                    bod_id: selectedItems,
                    user_preference: type
                }
            };
            switch (docType) {
                case 'alldocs':
                    await datasheetAutoSearch(data);
                    await omAutoSearch(data);
                    break;
                case 'datasheet':
                    await datasheetAutoSearch(data);
                    break;
                case 'installGuide':
                    await installGuideAutoSearch(data);
                    break;
                case 'warranty':
                    await warrantyAutoSearch(data);
                    break;
            }
            handleAutoSearchDialog();
            unCheckSelectedLineItems();
            setSnackbarMessage(autoSearchMsg);
            trackIntercomEvent('Auto_Select');
        } catch (error) {
            console.log(error);
        }
    };

    const handleReuseDatasheetDialog = () => {
        setOpenReuseDatasheetDialog((old) => !old);
    };

    const onReuse = () => {
        handleReuseDatasheetDialog();
        unCheckSelectedLineItems();
    };

    const onClickConfirm = (value: { reuse: boolean; document?: ReuseDialogType }) => {
        if (value.reuse) {
            setSelectedReuseDialogType(value.document || ReuseDialogType.WARRANTY);
            handleReuseDatasheetDialog();
        } else {
            unCheckSelectedLineItems();
        }
        handleToggleAddReuseDialog();
    };

    const handleToggleAddReuseDialog = () => {
        setOpenAddReuseDialog((old) => !old);
    };
    const onManageColumnsClick = () => {
        setOpenManageColumnsPopup(true);
    };

    const autoSearchLoading = useMemo(() => {
        switch (selectedDocType) {
            case 'alldocs':
                return datasheetAutoSearchLoading || omAutoSearchLoading;
            case 'datasheet':
                return datasheetAutoSearchLoading;
            case 'installGuide':
                return instAutoSearchLoading;
            default:
                return warrantyAutoSearchLoading;
        }
    }, [datasheetAutoSearchLoading, instAutoSearchLoading, omAutoSearchLoading, selectedDocType, warrantyAutoSearchLoading]);



    return (
        <>
            <Portal>
                <Snackbar open={Boolean(snackbarMessage)} onClose={() => setSnackbarMessage('')} message={snackbarMessage}></Snackbar>
            </Portal>
            <Box display="flex" justifyContent="flex-end">
                <Box display="flex" gap={2} alignItems="center">
                    <KebabNestedMenu onClickManageColumn={onManageColumnsClick} hasOption={hasOption} />
                    <Tooltip title={selectedItems.length ? 'Add or Reuse a Document from another product' : 'Select Item(s) First'} placement={'bottom'}>
                        <span>
                            <Button variant="outlined" color="secondary" disabled={!selectedItems.length} onClick={handleToggleAddReuseDialog}>
                                Add / Reuse Document
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={selectedItems.length ? '' : 'Select Item(s) First'} placement={'bottom'}>
                        <span>
                            <NestedMenu options={GET_AUTO_SEARCH_OPTIONS(onAutoSearch)} anchorOrigin={anchorOrigin} transformOrigin={transformOrigin}>
                                <Button variant="outlined" color="secondary" endIcon={<MoreVertIcon />} disabled={!selectedItems.length}>
                                    Auto-Select
                                </Button>
                            </NestedMenu>
                        </span>
                    </Tooltip>
                    <ImportProducts color="secondary" variant='outlined' sx={{ bgcolor: "white" }} />
                </Box>
            </Box>
            {openReuseDatasheetDialog && (
                <ReuseDatasheetDialog
                    open={openReuseDatasheetDialog}
                    handleDialog={handleReuseDatasheetDialog}
                    confirmAction={onReuse}
                    tableData={tableData}
                    selectedRowIds={selectedItems}
                    reuseType={selectedReuseDialogType}
                />
            )}
            {openAddReuseDialog && <AddReuseDocument open={openAddReuseDialog} onClose={handleToggleAddReuseDialog} onReuseConfirm={onClickConfirm} selectedRowIds={selectedItems} />}
            {showAutosearchDialog && autoSearchLoading && (
                <AutosearchLoaderDialog
                    open={showAutosearchDialog && autoSearchLoading}
                    onClose={handleAutoSearchDialog}
                    loadTime={getOmLoadTime(selectedItems)}
                    helperLink={
                        <Link to="/v2/settings/my-profile" component={RouterLink} underline="hover">
                            Customize behavior here.
                        </Link>
                    }
                />
            )}
        </>
    );
};

export default DocumentTabHeader;
